/**
 * batch-61 uitrusting-credits-maten (FO-445 + FO-455) — DTO's, Zod-schema's en
 * labels voor de Van Ysselt-saldo-synchronisatie en de maten-leesweergave.
 *
 * Eén gedeeld schema per mutatie-endpoint (CLAUDE.md § Validatie): de API
 * parseert ermee én het backoffice-formulier valideert er client-side mee.
 */
import { z } from 'zod'

// ─── Saldo-berichten (wachtrij) ──────────────────────────────────────────────

export const VANYSSELT_SALDO_BERICHT_SOORTEN = [
  'VOORRAAD_LEVERING',
  'EXTERNE_BESTELLING',
  'RETOUR',
  'CORRECTIE',
  // batch-62 uitrusting-inkoop (FO-466) - levering volgens een Van Ysselt-
  // pakbon/-factuur zonder STREEP-bestelling.
  'PAKBON_LEVERING',
] as const
export type VanYsseltSaldoBerichtSoort = (typeof VANYSSELT_SALDO_BERICHT_SOORTEN)[number]

export const VANYSSELT_SALDO_BERICHT_SOORT_LABELS: Record<VanYsseltSaldoBerichtSoort, string> = {
  VOORRAAD_LEVERING: 'Levering uit STREEP-voorraad',
  EXTERNE_BESTELLING: 'Externe bestelling (saldo-blok in bestelmail)',
  RETOUR: 'Retour',
  CORRECTIE: 'Correctie',
  PAKBON_LEVERING: 'Levering volgens pakbon/factuur (zonder bestelling)',
}

export const VANYSSELT_SALDO_BERICHT_STATUSSEN = [
  'WACHTEND',
  'VERZONDEN',
  'MISLUKT',
  'OVERGESLAGEN',
] as const
export type VanYsseltSaldoBerichtStatus = (typeof VANYSSELT_SALDO_BERICHT_STATUSSEN)[number]

export const VANYSSELT_SALDO_BERICHT_STATUS_LABELS: Record<VanYsseltSaldoBerichtStatus, string> = {
  WACHTEND: 'Wacht op verzending',
  VERZONDEN: 'Gedeeld met Van Ysselt',
  MISLUKT: 'Niet gedeeld (bezorgfout)',
  OVERGESLAGEN: 'Overgeslagen',
}

/** Eén regel in een saldo-bericht (bevroren op het mutatiemoment). */
export interface VanYsseltSaldoBerichtRegel {
  omschrijving: string
  maat: string | null
  aantal: number
  creditsPerStuk: number
  credits: number
}

export interface VanYsseltSaldoBerichtDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  entityId: string | null
  soort: VanYsseltSaldoBerichtSoort
  bestellingId: string | null
  creditMutatieId: string | null
  jaar: number
  kwartaal: number
  credits: number
  saldoVoor: number
  saldoNa: number
  gereserveerdNa: number
  regels: VanYsseltSaldoBerichtRegel[]
  bundelDatum: string
  status: VanYsseltSaldoBerichtStatus
  verstuurdOp: string | null
  outgoingEmailId: string | null
  pogingen: number
  laatsteFout: string | null
  createdAt: string
}

// ─── Deling per bestelling (portal + backoffice) ─────────────────────────────

/**
 * Samenvatting "is deze bestelling met Van Ysselt gedeeld" voor de portal en
 * het backoffice-detail. `null` op de bestelling = geen Van Ysselt-credits in
 * het spel (credit-vrij of andere leverancier).
 */
export interface VanYsseltDelingDTO {
  /** Alle berichtdelen verzonden. */
  gedeeld: boolean
  /** Moment van (volledige) deling, als ISO-string. */
  gedeeldOp: string | null
  /** Er staat minstens één deel op MISLUKT (bezorgfout): "niet gedeeld". */
  bezorgfout: boolean
  /** Aantal delen dat nog in de wachtrij staat. */
  wachtend: number
}

// ─── Afstemming ──────────────────────────────────────────────────────────────

export const VANYSSELT_AFSTEMMING_BRONNEN = ['HANDMATIG', 'MAIL_REPLY', 'MAANDOVERZICHT'] as const
export type VanYsseltAfstemmingBron = (typeof VANYSSELT_AFSTEMMING_BRONNEN)[number]

export const VANYSSELT_AFSTEMMING_BRON_LABELS: Record<VanYsseltAfstemmingBron, string> = {
  HANDMATIG: 'Handmatig geregistreerd',
  MAIL_REPLY: 'Reactie van Van Ysselt (mail)',
  MAANDOVERZICHT: 'Reactie op maandoverzicht',
}

export const VANYSSELT_AFSTEMMING_STATUSSEN = [
  'GELIJK',
  'AFWIJKING_OPEN',
  'GECORRIGEERD',
  'GEACCEPTEERD',
] as const
export type VanYsseltAfstemmingStatus = (typeof VANYSSELT_AFSTEMMING_STATUSSEN)[number]

export const VANYSSELT_AFSTEMMING_STATUS_LABELS: Record<VanYsseltAfstemmingStatus, string> = {
  GELIJK: 'Gelijk',
  AFWIJKING_OPEN: 'Afwijking (open)',
  GECORRIGEERD: 'Gecorrigeerd (STREEP zat fout)',
  GEACCEPTEERD: 'STREEP-saldo leidend (Van Ysselt geïnformeerd)',
}

export interface VanYsseltAfstemmingDTO {
  id: string
  employeeId: string
  employeeNaam: string | null
  jaar: number
  kwartaal: number
  peildatum: string
  externSaldo: number
  streepSaldo: number
  streepGereserveerd: number
  verschil: number
  bron: VanYsseltAfstemmingBron
  toelichting: string | null
  geregistreerdDoorId: string
  geregistreerdDoorNaam: string | null
  status: VanYsseltAfstemmingStatus
  correctieMutatieId: string | null
  afgehandeldOp: string | null
  afgehandeldDoorId: string | null
  afgehandeldDoorNaam: string | null
  afhandelReden: string | null
  createdAt: string
}

/** Mutatie-regel zoals het afstemmingsdetail hem toont (sinds de vorige afstemming). */
export interface VanYsseltAfstemmingMutatieDTO {
  id: string
  createdAt: string
  richting: string
  credits: number
  bron: string
  bestellingId: string | null
  toelichting: string | null
  saldoVoor: number | null
  saldoNa: number | null
  gedeeldOp: string | null
}

export interface VanYsseltAfstemmingDetailDTO {
  afstemming: VanYsseltAfstemmingDTO
  /** Vorige afstemming van dezelfde medewerker (of null). */
  vorigeAfstemming: VanYsseltAfstemmingDTO | null
  /** Mutaties sinds de vorige afstemming (of alle mutaties van het kwartaal). */
  mutatiesSinds: VanYsseltAfstemmingMutatieDTO[]
  /** Huidig STREEP-saldo (live, ter vergelijking met het snapshot). */
  huidigSaldo: { toegekend: number; gebruikt: number; gereserveerd: number; beschikbaar: number } | null
  /** De correctie zou het beschikbare saldo negatief maken (waarschuwing, geen blokkade). */
  correctieMaaktNegatief: boolean
}

export const RegistreerAfstemmingSchema = z.object({
  employeeId: z.string().min(1, 'Kies een medewerker'),
  jaar: z.number().int().min(2020).max(2100),
  kwartaal: z.number().int().min(1).max(4),
  /** ISO-datum (yyyy-MM-dd) van de melding; default vandaag. */
  peildatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum')
    .optional(),
  externSaldo: z.number().int().min(-100000).max(100000),
  bron: z.enum(VANYSSELT_AFSTEMMING_BRONNEN).optional(),
  toelichting: z.string().trim().max(2000).optional().nullable(),
})
export type RegistreerAfstemmingInput = z.infer<typeof RegistreerAfstemmingSchema>

export const CorrigeerAfstemmingSchema = z.object({
  reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens)').max(2000),
  /** Bewuste bevestiging wanneer het beschikbare saldo negatief wordt. */
  negatiefToegestaan: z.boolean().optional(),
})
export type CorrigeerAfstemmingInput = z.infer<typeof CorrigeerAfstemmingSchema>

export const AccepteerAfstemmingSchema = z.object({
  reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens)').max(2000),
})
export type AccepteerAfstemmingInput = z.infer<typeof AccepteerAfstemmingSchema>

// ─── Retour ──────────────────────────────────────────────────────────────────

export const RegelRetourSchema = z.object({
  aantal: z.number().int().min(1),
  reden: z.string().trim().min(3, 'Reden is verplicht (minimaal 3 tekens)').max(2000),
  /**
   * batch-62 uitrusting-inkoop (FO-467 §10) — bij een regel die zonder
   * voorraadmutatie is uitgegeven: de voorraadlocatie waar het artikel terug
   * in gaat (stand +n, telverschil opgeheven). Anders niet nodig.
   */
  locatieId: z.string().min(1).nullable().optional(),
})
export type RegelRetourInput = z.infer<typeof RegelRetourSchema>

export interface RegelRetourResultDTO {
  retourId: string
  creditsTerug: number
  creditMutatieId: string | null
  /** Er is een retourbericht in de wachtrij gezet (alleen bij Van Ysselt-credits). */
  berichtIngepland: boolean
  /** batch-62 (FO-467) — voorraadkant van een zonder-mutatie-uitgifte, of null. */
  voorraad?: { voorraadGeboekt: number; verschilOpgeheven: number } | null
}

// ─── Maandoverzicht ──────────────────────────────────────────────────────────

export const VANYSSELT_MAANDOVERZICHT_STATUSSEN = ['VERZONDEN', 'MISLUKT', 'OVERGESLAGEN'] as const
export type VanYsseltMaandoverzichtStatus = (typeof VANYSSELT_MAANDOVERZICHT_STATUSSEN)[number]

export interface VanYsseltMaandoverzichtDTO {
  id: string
  jaar: number
  maand: number
  status: VanYsseltMaandoverzichtStatus
  aantalMedewerkers: number
  aantalMutaties: number
  bestandsnaam: string | null
  outgoingEmailId: string | null
  fout: string | null
  verstuurdOp: string | null
  verstuurdDoorId: string | null
  createdAt: string
}

export const VerstuurMaandoverzichtSchema = z.object({
  jaar: z.number().int().min(2020).max(2100),
  maand: z.number().int().min(1).max(12),
  /** Een al verzonden overzicht opnieuw sturen (nieuwe mail, zelfde rij). */
  opnieuw: z.boolean().optional(),
})
export type VerstuurMaandoverzichtInput = z.infer<typeof VerstuurMaandoverzichtSchema>

// ─── Instellingen ────────────────────────────────────────────────────────────

/** Bundelmodus van de saldo-berichten (FO-445 §3B.4): per dag (default) of direct. */
export const VANYSSELT_BUNDEL_MODI = ['DAG', 'DIRECT'] as const
export type VanYsseltBundelModus = (typeof VANYSSELT_BUNDEL_MODI)[number]

export const VANYSSELT_BUNDEL_MODUS_LABELS: Record<VanYsseltBundelModus, string> = {
  DAG: 'Eén bericht per medewerker per dag (dagbundel)',
  DIRECT: 'Direct bij elke levering',
}

/** Sleutels in `NotificatieInstelling` (key/value), zelfde patroon als klantverzoeken. */
export const VANYSSELT_SYNC_INSTELLING_KEYS = {
  BUNDEL_MODUS: 'uitrusting.vanYsseltSaldoBundelModus',
  MAANDOVERZICHT_ACTIEF: 'uitrusting.vanYsseltMaandoverzichtActief',
  /** Uur (NL-tijd, 0-23) waarop de dagbundel van vandaag verstuurd mag worden. */
  DAGBUNDEL_UUR: 'uitrusting.vanYsseltDagbundelUur',
} as const

export const VanYsseltSyncInstellingenSchema = z.object({
  bundelModus: z.enum(VANYSSELT_BUNDEL_MODI),
  maandoverzichtActief: z.boolean(),
  dagbundelUur: z.number().int().min(0).max(23),
})
export type VanYsseltSyncInstellingenInput = z.infer<typeof VanYsseltSyncInstellingenSchema>

export const VANYSSELT_SYNC_INSTELLINGEN_DEFAULTS: VanYsseltSyncInstellingenInput = {
  bundelModus: 'DAG',
  maandoverzichtActief: true,
  dagbundelUur: 17,
}

export interface VanYsseltSyncInstellingenDTO extends VanYsseltSyncInstellingenInput {
  /** De leverancier die de berichten ontvangt (uit de uitrusting-instellingen of op naam). */
  ontvanger: { leverancierId: string; naam: string; email: string | null } | null
}

// ─── Dekking (guard) ─────────────────────────────────────────────────────────

/**
 * Uitkomst van de mutatie-guard "geen Van Ysselt-levering zonder mutatie en
 * omgekeerd" (FO-445 §6.4). Leeg = alles gedekt.
 */
export interface VanYsseltDekkingDTO {
  /** Verwerkte/geleverde Van Ysselt-bestellingen met credits maar zonder afname-mutatie. */
  bestellingenZonderMutatie: Array<{ bestellingId: string; employeeId: string; creditsTotaal: number }>
  /** Afname-mutaties die geen bestaande, goedgekeurde bestelling (meer) hebben. */
  mutatiesZonderLevering: Array<{ creditMutatieId: string; employeeId: string; bestellingId: string | null }>
  /** Verwerkte Van Ysselt-bestellingen met credits waarvan geen enkel deel een bericht kent. */
  bestellingenZonderBericht: Array<{ bestellingId: string; employeeId: string }>
  gecontroleerdOp: string
}

// ─── Credit-richting-labels (incl. retour) ───────────────────────────────────

export const CREDIT_RICHTING_LABELS: Record<string, string> = {
  kwartaaltoekenning: 'Kwartaaltoekenning',
  reservering: 'Reservering',
  afname: 'Afname',
  vrijgave: 'Vrijgave',
  verval: 'Verval',
  correctie: 'Correctie',
  retour: 'Retour',
}

// ─── FO-455 — maten leesweergave ─────────────────────────────────────────────

export const MAAT_TYPES = ['boven', 'broek', 'schoen', 'handschoen', 'helm'] as const
export type MaatType = (typeof MAAT_TYPES)[number]

export const MAAT_TYPE_LABELS: Record<MaatType, string> = {
  boven: 'Bovenkleding',
  broek: 'Broek',
  schoen: 'Schoen',
  handschoen: 'Handschoen',
  helm: 'Helm',
}

/** Korte labels voor de informatieve regel ("broek 52, shirt L, schoen 44"). */
export const MAAT_TYPE_KORT: Record<MaatType, string> = {
  boven: 'shirt',
  broek: 'broek',
  schoen: 'schoen',
  handschoen: 'handschoen',
  helm: 'helm',
}

export const MATEN_WIJZIGING_BRONNEN = ['PROFIEL', 'UITRUSTING', 'PORTAL', 'ONBOARDING'] as const
export type MatenWijzigingBron = (typeof MATEN_WIJZIGING_BRONNEN)[number]

export const MATEN_WIJZIGING_BRON_LABELS: Record<MatenWijzigingBron, string> = {
  PROFIEL: 'medewerkersprofiel',
  UITRUSTING: 'Uitrusting > Werknemersmaten',
  PORTAL: 'de medewerker zelf (portal)',
  ONBOARDING: 'onboarding-formulier',
}

/**
 * Read-DTO van de maten (FO-455): dezelfde vorm als de edit-DTO
 * (`WerknemersMaatDTO`) plus "laatst gewijzigd op/door". Gate: MEDEWERKERS.canView
 * óf de uitrusting-/magazijnrol (UITRUSTING/VOORRAAD canView).
 */
export interface MedewerkerMatenDTO {
  employeeId: string
  naam: string
  kledingmaatBoven: string | null
  kledingmaatBroek: string | null
  schoenmaat: string | null
  handschoenmaat: string | null
  helmmaat: string | null
  maatOpmerking: string | null
  laatstGewijzigdOp: string | null
  laatstGewijzigdDoorNaam: string | null
  laatstGewijzigdBron: MatenWijzigingBron | null
}
